import type { Language } from '../../i18n';
import { getSkillLabel } from '@utils/skillLabels';

type CopyFn = (en: string, de: string, hr: string) => string;

type PlanTeamSkillsCardProps = {
	recommendedTeamSize?: number | null;
	requiredSkills: string[];
	copy: CopyFn;
	language: Language;
	className?: string;
};

export default function PlanTeamSkillsCard({
	recommendedTeamSize,
	requiredSkills,
	copy,
	language,
	className = ''
}: PlanTeamSkillsCardProps) {
	const hasTeam = recommendedTeamSize != null && recommendedTeamSize >= 1;
	const hasSkills = requiredSkills.length > 0;
	if (!hasTeam && !hasSkills) return null;

	return (
		<div className={`card mb-4 ${className}`.trim()}>
			<div className="card-header">
				<h5 className="mb-0">
					<i className="bi bi-person-gear text-success me-2" aria-hidden />
					{copy('Team & skills', 'Team und Kompetenzen', 'Tim i vještine')}
				</h5>
			</div>
			<div className="card-body">
				{hasTeam && (
					<div className="mb-3">
						<div className="text-muted small mb-1 fw-semibold">
							{copy('Recommended team size', 'Empfohlene Teamgröße', 'Preporučena veličina tima')}
						</div>
						<div className="fw-bold text-success">
							{recommendedTeamSize}{' '}
							{recommendedTeamSize === 1
								? copy('person', 'Person', 'osoba')
								: copy('people', 'Personen', 'osobe')}
						</div>
						<div className="text-muted small mt-0">
							{copy(
								'to execute this plan in a realistic timeframe',
								'um diesen Plan in einem realistischen Zeitrahmen umzusetzen',
								'za izvedbu ovog plana u realnom vremenskom okviru'
							)}
						</div>
					</div>
				)}
				{hasSkills && (
					<>
						{hasTeam && <hr className="my-2" />}
						<div className="text-muted small mb-1 fw-semibold">
							{copy('Skills needed', 'Benötigte Kompetenzen', 'Potrebne vještine')}
						</div>
						<div className="d-flex flex-wrap gap-1">
							{requiredSkills.map((skill) => (
								<span key={skill} className="badge bg-success-subtle text-success-emphasis">
									{getSkillLabel(skill, language)}
								</span>
							))}
						</div>
					</>
				)}
			</div>
		</div>
	);
}
